import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

const personas = {
  growth: {
    name: "In-house Growth Teams",
    title: "VP/Dir., User Acquisition",
    tier: "Elite & Enterprise €€€€",
    quote:
      "Cover every market and both stores without hiring another ASO specialist — and lower my blended CAC against a board-reportable owned channel.",
    examples: "e.g., Rovio, King, FunPlus, Duolingo",
  },
  agency: {
    name: "ASO Agencies",
    title: "Head of ASO",
    tier: "Elite & Enterprise €€€€",
    quote:
      "Run my methodology on every client, every day — demonstrable ROI my clients see in their own numbers. Retention up, capacity up.",
    examples: "e.g., Phiture, Gummicube, Yodel Mobile",
  },
  indie: {
    name: "Indie Devs & Small Studios",
    title: "Owner-Operator",
    tier: "Basic & Pro €",
    quote:
      "Get expert ASO running on my apps without learning ASO myself — revenue and runway, with no ASO hire.",
    examples: "e.g., Pickem, TRMNL4, Fabulous, GeekLab",
  },
  freelance: {
    name: "Freelance ASO Consultants",
    title: "Principal",
    tier: "Pro & Elite €€€",
    affiliate: true,
    quote:
      "Run the daily expert audit across every client I serve — attributable wins I can put my name behind. Reputation, then more clients.",
    examples: "e.g., Julie Tonna, Thomas Petit, Hannah Parvaz",
  },
} as const;

export default function TheCustomer() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8">
      <SlideHeader eyebrow="The Customer">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          Four buyers, same pains.
        </h1>
      </SlideHeader>

      <div className="flex flex-col gap-3 md:hidden">
        <MobilePersonaCard
          axis="Large Firm · App / Game Publisher"
          {...personas.growth}
        />
        <MobilePersonaCard
          axis="Large Firm · ASO Professional"
          {...personas.agency}
        />
        <MobilePersonaCard
          axis="Small Firm · App / Game Publisher"
          {...personas.indie}
        />
        <MobilePersonaCard
          axis="Small Firm · ASO Professional"
          {...personas.freelance}
        />
      </div>

      <div className="-mt-6 hidden md:grid md:flex-1 md:grid-cols-[140px_1fr_1fr] md:grid-rows-[auto_minmax(0,1fr)_minmax(0,1fr)]">
        {/* Row color bands — span all 3 columns. -mr-4 extends 12px past the ASO Professionals column right edge. */}
        <div className="col-start-1 col-end-4 row-start-2 row-end-3 -mr-4 my-[5px] bg-blue-200/25 ring-2 ring-inset ring-blue-300" />
        <div className="col-start-1 col-end-4 row-start-3 row-end-4 -mr-4 my-[5px] bg-emerald-200/25 ring-2 ring-inset ring-emerald-300" />
        {/* Column color bands — span all 3 rows. mt-[2px] keeps a 2px top inset so col strokes don't overlay the row top stroke. -mb-4 extends 12px past the Small Firms row bottom edge. */}
        <div className="col-start-2 col-end-3 row-start-1 row-end-4 mt-[2px] mx-[5px] -mb-4 bg-amber-200/25 ring-2 ring-inset ring-amber-300" />
        <div className="col-start-3 col-end-4 row-start-1 row-end-4 mt-[2px] mx-[5px] -mb-4 bg-rose-200/25 ring-2 ring-inset ring-rose-300" />

        {/* Content cells — explicit positions, render on top of color bands */}
        <div className="col-start-1 row-start-1" />
        <div className="col-start-2 row-start-1 flex items-center justify-center py-5">
          <AxisLabel>App / Game Publishers</AxisLabel>
        </div>
        <div className="col-start-3 row-start-1 flex items-center justify-center py-5">
          <AxisLabel>ASO Professionals</AxisLabel>
        </div>

        <div className="col-start-1 row-start-2 flex items-center justify-center">
          <AxisLabel className="text-center">
            Large
            <br />
            Firms
          </AxisLabel>
        </div>
        <div className="col-start-2 row-start-2 p-5">
          <PersonaCell {...personas.growth} />
        </div>
        <div className="col-start-3 row-start-2 p-5">
          <PersonaCell {...personas.agency} />
        </div>

        <div className="col-start-1 row-start-3 flex items-center justify-center">
          <AxisLabel className="text-center">Small Firms</AxisLabel>
        </div>
        <div className="col-start-2 row-start-3 p-5">
          <PersonaCell {...personas.indie} />
        </div>
        <div className="col-start-3 row-start-3 p-5">
          <PersonaCell {...personas.freelance} />
        </div>
      </div>
    </div>
  );
}

function AxisLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={`text-xl font-semibold uppercase leading-tight tracking-wider text-slate-500 ${className ?? ""}`}
    >
      {children}
    </p>
  );
}

function PersonaCell({
  name,
  title,
  tier,
  quote,
  examples,
  affiliate,
}: {
  name: string;
  title: string;
  tier: string;
  quote: string;
  examples: string;
  affiliate?: boolean;
}) {
  return (
    <div className="flex h-full flex-col rounded-xl bg-white p-6 shadow-md ring-2 ring-slate-300">
      <p className="text-2xl font-semibold tracking-tight">{name}</p>
      <p className="mt-1 text-sm italic text-slate-500">{examples}</p>
      <p className="mt-4 text-lg italic leading-snug text-slate-700">
        &ldquo;{quote}&rdquo;
      </p>
      <p className="mt-2 text-sm font-medium text-slate-500">- {title}</p>
      <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
        <span className="inline-block rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white">
          {tier}
        </span>
        {affiliate ? (
          <span className="inline-block rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white">
            Affiliate
          </span>
        ) : null}
      </div>
    </div>
  );
}

function MobilePersonaCard({
  axis,
  name,
  title,
  tier,
  quote,
  examples,
  affiliate,
}: {
  axis: string;
  name: string;
  title: string;
  tier: string;
  quote: string;
  examples: string;
  affiliate?: boolean;
}) {
  return (
    <div className="rounded-xl bg-white p-4 shadow-md ring-2 ring-slate-300">
      <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">
        {axis}
      </p>
      <p className="mt-1.5 text-xl font-semibold tracking-tight text-slate-900">
        {name}
      </p>
      <p className="mt-1 text-xs italic text-slate-500">{examples}</p>
      <p className="mt-3 text-sm italic leading-snug text-slate-700">
        &ldquo;{quote}&rdquo;
      </p>
      <p className="mt-1.5 text-xs font-medium text-slate-500">- {title}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="inline-block rounded-full bg-blue-600 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
          {tier}
        </span>
        {affiliate ? (
          <span className="inline-block rounded-full bg-emerald-600 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
            Affiliate
          </span>
        ) : null}
      </div>
    </div>
  );
}
